/**
 * Proactive nudge engine — the agent that taps you on the shoulder.
 *
 * `proactiveNudges()` turns live state into a small, ranked list of
 * contextual suggestions ("Aisha Khan is at-risk — draft a check-in?",
 * "Ben Foster invoice 5d overdue — send a reminder?"), each carrying a
 * concrete, GOVERNED `AgentAction`. The nudge itself never writes anything:
 * the suggested action routes through the EXISTING approval/proposal flow
 * (`queueApproval` → Dashboard inbox → `resolveApproval`), so every nudge
 * the operator accepts is audited, gated, and undoable exactly like any
 * other agent proposal.
 *
 * It invents nothing — every nudge is drawn from a PURE signal the app
 * already computes:
 *
 *   - at-risk customers ......... `rankCustomersByRisk` (crm/health.ts),
 *                                 suggesting the same check-in task the
 *                                 customer-health agent proposes
 *   - past-due invoices ......... unpaid `billingDates` in the past (the
 *                                 scanner's "Billing past due" rule)
 *   - overdue follow-ups ........ `followUpDate` fully past on a live
 *                                 relationship (weeklyOps semantics)
 *   - renewals bearing down ..... `renewalDate` within 14 days
 *   - high scan findings ........ `ScanFinding[]` from `scanSystem`
 *                                 (caller passes them in; engine stays pure)
 *
 * Ranking mirrors weeklyOps: a fixed per-kind base (`NUDGE_KIND_BASE`) plus
 * a bounded, explainable magnitude. Nudges are DEDUPED to one per entity
 * (highest score wins) so the same slipping account can't spam three
 * suggestions at once. PURE by contract: no store, no network, no clock
 * reads unless `now` is omitted, no input mutation. A clean book yields an
 * empty list — the agent never manufactures urgency.
 */

import type { Person, ProjectTask } from '../db/types';
import type { AgentAction, CreateTaskAction, ScheduleFollowUpAction } from './types';
import type { ScanFinding } from './systemScanner';
import { rankCustomersByRisk } from '../crm/health';
import { checkinTaskFor } from './customerHealthAgent';

export type NudgeKind =
  | 'invoice_overdue'
  | 'at_risk_customer'
  | 'critical_finding'
  | 'renewal_soon'
  | 'followup_overdue';

/** One proactive suggestion, ready to render + queue for approval. */
export interface Nudge {
  /** Stable id: `nudge:<kind>:<entityId>`. */
  id: string;
  kind: NudgeKind;
  /** The record this nudge is about (person id or finding id). */
  entityId: string;
  /** Set when the nudge ties to a contact — lets UIs deep-link. */
  personId?: string;
  /** "Aisha Khan is at-risk" — the observation. */
  headline: string;
  /** "Draft a check-in?" — the offer, phrased as a question. */
  suggestion: string;
  /** The honest "why" line, drawn from record data. */
  reason: string;
  /** Button label for the governed action, e.g. "Draft check-in". */
  actionLabel: string;
  /** The concrete governed write to queue into the approvals inbox. */
  action: AgentAction;
  /** Kind base + bounded magnitude — higher = surface it first. */
  priorityScore: number;
}

export interface NudgeInput {
  people?: Person[];
  /** Project tasks — folded into customer health scoring. */
  tasks?: ProjectTask[];
  /** Pre-computed scan findings (from `scanSystem`); only `high` nudge. */
  findings?: ScanFinding[];
  /** Injectable clock for deterministic tests; defaults to now. */
  now?: number;
}

/**
 * Fixed per-kind score base — exported so the ranking is inspectable and
 * the UI can explain "why is this above that?". Magnitude bonuses are
 * bounded per kind (`MAX_MAGNITUDE`) so no single signal runs away.
 */
export const NUDGE_KIND_BASE: Record<NudgeKind, number> = {
  invoice_overdue: 70,
  at_risk_customer: 60,
  critical_finding: 55,
  renewal_soon: 50,
  followup_overdue: 40,
};

const MAX_MAGNITUDE: Record<NudgeKind, number> = {
  invoice_overdue: 30, // days past due, capped
  at_risk_customer: 40, // health deficit (100 − score), capped
  critical_finding: 25, // scanner sortKey, capped
  renewal_soon: 14, // proximity (14 − days out), capped
  followup_overdue: 30, // days overdue, capped
};

const DAY_MS = 86_400_000;

function score(kind: NudgeKind, magnitude: number): number {
  const bounded = Math.max(0, Math.min(MAX_MAGNITUDE[kind], Math.round(magnitude)));
  return NUDGE_KIND_BASE[kind] + bounded;
}

/** Whole days a date (YYYY-MM-DD or ISO) is past `now`; ≤ 0 = not yet. */
function daysPast(iso: string, now: number): number {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.floor((now - t) / DAY_MS);
}

/** Plain YYYY-MM-DD for `now + days` — used for suggested reschedules. */
function isoDatePlus(now: number, days: number): string {
  return new Date(now + days * DAY_MS).toISOString().slice(0, 10);
}

function dLabel(days: number): string {
  return `${days}d`;
}

// ---------------------------------------------------------------------------
// Per-signal collectors — each PURE, each producing zero nudges from nothing.
// ---------------------------------------------------------------------------

/** Past-due, unpaid billing milestones — one nudge per customer (worst). */
function collectOverdueInvoices(people: Person[], now: number): Nudge[] {
  const out: Nudge[] = [];
  for (const p of people) {
    if (p.category !== 'customer' || p.status === 'churned') continue;
    const overdue = (p.billingDates ?? [])
      .filter((bd) => (bd.status ?? 'scheduled') !== 'paid' && daysPast(bd.date, now) > 0)
      .sort((a, b) => daysPast(b.date, now) - daysPast(a.date, now));
    const worst = overdue[0];
    if (!worst) continue;
    const days = daysPast(worst.date, now);
    const action: CreateTaskAction = {
      kind: 'create_task',
      title: `Send payment reminder — ${p.name}`,
      priority: 'urgent',
      dueDate: null,
      personId: p.id,
      personName: p.name,
      // The dollars at risk on this reminder — never invented.
      value: worst.amount,
    };
    out.push({
      id: `nudge:invoice_overdue:${p.id}`,
      kind: 'invoice_overdue',
      entityId: p.id,
      personId: p.id,
      headline: `${p.name} invoice ${dLabel(days)} overdue`,
      suggestion: 'Send a reminder?',
      reason: `${worst.label} was due ${worst.date}${worst.amount ? ` · $${worst.amount}` : ''}${
        overdue.length > 1 ? ` · ${overdue.length} invoices past due` : ''
      }`,
      actionLabel: 'Queue reminder task',
      action,
      priorityScore: score('invoice_overdue', days),
    });
  }
  return out;
}

/** At-risk live customers — suggests the health agent's own check-in task. */
function collectAtRisk(people: Person[], tasks: ProjectTask[], now: number): Nudge[] {
  return rankCustomersByRisk(people, { tasks, now })
    .filter((r) => r.health.band === 'at-risk')
    .map(({ person, health }) => {
      const topFactor = [...health.factors]
        .filter((f) => f.points < 0)
        .sort((a, b) => a.points - b.points)[0];
      return {
        id: `nudge:at_risk_customer:${person.id}`,
        kind: 'at_risk_customer' as const,
        entityId: person.id,
        personId: person.id,
        headline: `${person.name} is at-risk`,
        suggestion: 'Draft a check-in?',
        reason: `Health ${health.score}/100${topFactor ? ` — ${topFactor.label}` : ''}`,
        actionLabel: 'Draft check-in',
        // Reuse the EXACT task the customer-health agent proposes, so the
        // approvals inbox de-dupes across both entry points.
        action: checkinTaskFor(person, 'at-risk'),
        priorityScore: score('at_risk_customer', 100 - health.score),
      };
    });
}

/** Renewals inside 14 days on live customers — prep before the cliff. */
function collectRenewalsSoon(people: Person[], now: number): Nudge[] {
  const out: Nudge[] = [];
  for (const p of people) {
    if (p.category !== 'customer' || p.status === 'churned') continue;
    if (!p.renewalDate) continue;
    const t = Date.parse(p.renewalDate);
    if (Number.isNaN(t) || t < now) continue; // lapsed → health handles it
    const daysOut = Math.ceil((t - now) / DAY_MS);
    if (daysOut > 14) continue;
    const action: CreateTaskAction = {
      kind: 'create_task',
      title: `Prep renewal — ${p.name}`,
      priority: 'high',
      dueDate: p.renewalDate,
      personId: p.id,
      personName: p.name,
      value: p.accountValue,
    };
    out.push({
      id: `nudge:renewal_soon:${p.id}`,
      kind: 'renewal_soon',
      entityId: p.id,
      personId: p.id,
      headline: `${p.name} renews in ${dLabel(daysOut)}`,
      suggestion: 'Prep the renewal?',
      reason: `Renewal ${p.renewalDate}${p.accountValue ? ` · $${p.accountValue} ARR at stake` : ''}`,
      actionLabel: 'Queue renewal prep',
      action,
      priorityScore: score('renewal_soon', 14 - daysOut),
    });
  }
  return out;
}

/** Follow-ups fully past their day on live relationships. */
function collectOverdueFollowUps(people: Person[], now: number): Nudge[] {
  const out: Nudge[] = [];
  for (const p of people) {
    if (!p.followUpDate) continue;
    if (p.stage === 'closed-lost' || p.status === 'churned') continue;
    // Grant the due day itself — overdue only once the day has fully passed.
    const t = Date.parse(p.followUpDate);
    if (Number.isNaN(t) || t + DAY_MS > now) continue;
    const days = daysPast(p.followUpDate, now);
    const action: ScheduleFollowUpAction = {
      kind: 'schedule_follow_up',
      personId: p.id,
      personName: p.name,
      followUpDate: isoDatePlus(now, 3),
      note: p.nextStep ?? `Rescheduled — was due ${p.followUpDate}`,
    };
    out.push({
      id: `nudge:followup_overdue:${p.id}`,
      kind: 'followup_overdue',
      entityId: p.id,
      personId: p.id,
      headline: `Follow-up with ${p.name} ${dLabel(days)} overdue`,
      suggestion: 'Reschedule it?',
      reason: `Was due ${p.followUpDate}${p.nextStep ? ` · next step: ${p.nextStep}` : ''}`,
      actionLabel: 'Reschedule follow-up',
      action,
      priorityScore: score('followup_overdue', days),
    });
  }
  return out;
}

/** High-severity scanner findings — offer a governed triage task. */
function collectHighFindings(findings: ScanFinding[]): Nudge[] {
  return findings
    .filter((f) => f.severity === 'high')
    .map((f) => {
      const action: CreateTaskAction = {
        kind: 'create_task',
        title: `Triage: ${f.title}`,
        priority: 'urgent',
        dueDate: null,
      };
      return {
        id: `nudge:critical_finding:${f.id}`,
        kind: 'critical_finding' as const,
        entityId: f.id,
        headline: f.title,
        suggestion: 'Queue a triage task?',
        reason: `${f.category} — ${f.detail}`,
        actionLabel: 'Queue triage task',
        action,
        priorityScore: score('critical_finding', f.sortKey),
      };
    });
}

// ---------------------------------------------------------------------------
// The engine
// ---------------------------------------------------------------------------

/**
 * Produce the ranked, deduped nudge list from the live snapshots. PURE:
 * reads only its arguments; returns a new array sorted highest-priority
 * first (ties break alphabetically by headline, then id, for determinism).
 * One nudge per entity — when a record trips several signals, the
 * highest-scoring suggestion wins and the rest stay quiet.
 */
export function proactiveNudges(input: NudgeInput): Nudge[] {
  const now = input.now ?? Date.now();
  const people = input.people ?? [];
  const tasks = input.tasks ?? [];

  const all: Nudge[] = [
    ...collectOverdueInvoices(people, now),
    ...collectAtRisk(people, tasks, now),
    ...collectRenewalsSoon(people, now),
    ...collectOverdueFollowUps(people, now),
    ...collectHighFindings(input.findings ?? []),
  ];

  // Dedupe to ONE nudge per entity: highest score wins; ties resolve by
  // kind base (a bigger base = a more actionable class of suggestion).
  const byEntity = new Map<string, Nudge>();
  for (const n of all) {
    const held = byEntity.get(n.entityId);
    if (
      !held ||
      n.priorityScore > held.priorityScore ||
      (n.priorityScore === held.priorityScore &&
        NUDGE_KIND_BASE[n.kind] > NUDGE_KIND_BASE[held.kind])
    ) {
      byEntity.set(n.entityId, n);
    }
  }

  return [...byEntity.values()].sort(
    (a, b) =>
      b.priorityScore - a.priorityScore ||
      a.headline.localeCompare(b.headline) ||
      a.id.localeCompare(b.id),
  );
}

/** The top N nudges — what the Copilot dock and view strips render. */
export function topNudges(nudges: Nudge[], limit = 3): Nudge[] {
  return nudges.slice(0, Math.max(0, limit));
}
